/**
 * What country this hold stands in, said in colour and in light.
 *
 * `WorldConfig.biome` has carried the province's terrain into the settlement
 * generator since `7071729`: a mountain capital is generated with 47,022 stone
 * and 19,725 iron tiles against a forest one's 4,298 and 1,569, and a marsh
 * capital with 57,150 marsh tiles. **The simulation already differed by country
 * and the renderer did not know.** Every hold in the game was drawn out of one
 * twelve-entry palette, so a hold in the Hoarfast and a hold in the deep wood
 * came out of the same box of paints with a different number of trees in it.
 *
 * This file is the renderer's half of that: one theme per `ProvinceTerrain`,
 * read once when the world changes and never again.
 *
 * ## The shape of a theme
 *
 * Not a colour per terrain per country — that is a hundred and twenty numbers
 * nobody can keep in step, and it is the mistake the campaign chart made before
 * `2c376ec` rebuilt it on one vocabulary. A theme is instead:
 *
 *   • a **pull** per ground *group* — dry, wood, rock, wet, water, built — a
 *     colour and a weight, applied over the baseline palette that shipped. So a
 *     country says "my bare ground is snow, hard" and every terrain that is
 *     bare ground moves, including ones added later.
 *   • the **light**: what the sun is and what the shadow is. Snow is lit
 *     blue-white and shadowed deep blue; sand is lit ochre and shadowed violet.
 *     This is the half that makes two grey grounds read as two places.
 *   • how much **shape** the ground has (`relief`), because a fen is flat and a
 *     massif is not, and the hillshade in `ground.ts` is the same field lit
 *     harder or softer.
 *   • which **fabric** open ground wears — drift, dune, tussock, scree — the
 *     vocabulary shared with `ui/relief.ts` and named the same way.
 *   • a **tint for the furniture**, so the trees, boulders and wrecks standing
 *     on this ground belong to it. One `Sprite.tint` per prop, no new art.
 *
 * ## The one rule that outranks all of it
 *
 * `data/palette.ts` says it and it is worth more than any amount of detail:
 * **terrain sits far below the units in value on purpose**, so that a 300-man
 * brawl does not turn to soup. A snowfield is the obvious way to break that.
 * So every colour here — and every colour the light can produce from it — is
 * held under `TERRAIN_MAX_LUMA`, and `tests/cityart.test.ts` walks the whole
 * table and the whole lighting range to prove it. Richer ground, not louder
 * ground.
 *
 * Pure numbers and pure functions: no Pixi, no DOM, nothing to bake. That is
 * what lets the cap be a test rather than a promise.
 */

import { C, mix } from '../data/palette.ts';
import { Terrain } from '../sim/types.ts';

/**
 * The brightest any tile of ground may ever be, as ITU-R BT.601 luma over
 * 0..255.
 *
 * Measured off the palette that shipped: the twelve `C.terr*` colours run from
 * 21 (deep water) to 63 (a bridge deck), and the units they have to sit under
 * are baked at `shade(cloth, 1.35)` and up — luma 110 and beyond. 78 is a
 * deliberate quarter-stop above the brightest ground in the game, which is
 * enough for a lit snow crest to read as snow and still leave a clear stop
 * between the ground and the cheapest militiaman on it.
 *
 * The cap is applied to the **final pixel**, after the light and after the
 * fabric, and not merely to the table — a base colour under the cap can be lit
 * straight through it, which is exactly how a snowfield would have got out.
 */
export const TERRAIN_MAX_LUMA = 78;

/**
 * The same, for the crisp *marks* the tile textures draw over the ground.
 *
 * Higher than the field's cap, and deliberately. The rule in `palette.ts` is
 * about the ground not competing with the men on it, and what competes is
 * **area**: a field of snow at luma 90 is soup, and a scatter of quartz chips
 * at 90 over two per cent of that field is what makes it read as rock. The
 * marks are small, sparse and sit inside a surface that is capped hard.
 *
 * 96 is where the brightest thing on the old tiles already sat — the lit facet
 * of a stone chip, at 106 — brought down a stop rather than left alone, since
 * the ground under it is now lit as well and the two used to be independent.
 */
export const MARK_MAX_LUMA = 96;

/** BT.601 luma of a packed RGB colour, 0..255. */
export function luma(c: number): number {
  return 0.299 * ((c >> 16) & 0xff) + 0.587 * ((c >> 8) & 0xff) + 0.114 * (c & 0xff);
}

/**
 * The same colour, no brighter than the cap.
 *
 * Scaled rather than clamped per channel: clamping shifts hue toward whichever
 * channel is highest, which turns capped snow green. Scaling keeps the colour
 * and only spends the value.
 */
export function capLuma(c: number, max = TERRAIN_MAX_LUMA): number {
  const l = luma(c);
  if (l <= max) return c;
  const k = max / l;
  const r = Math.min(255, Math.round(((c >> 16) & 0xff) * k));
  const g = Math.min(255, Math.round(((c >> 8) & 0xff) * k));
  const b = Math.min(255, Math.round((c & 0xff) * k));
  return (r << 16) | (g << 8) | b;
}

/**
 * The fabric vocabulary — what a surface's grain is made of.
 *
 * The same list the campaign chart settled on, named the same way, because the
 * whole point of this pass is that the two maps stop being two games. `ground.ts`
 * owns the shapers; this file only chooses.
 */
export type FabricKind =
  /** Ash and cinder: the baseline, blotchy and directionless. */
  | 'grit'
  /** Tussock and old field lines: worked open country. */
  | 'turf'
  /** Leaf mould, root and needle: the floor under a canopy. */
  | 'litter'
  /** Broken rock: plates and shatter. */
  | 'scree'
  /** Sand combed along one warped axis, with a ripple over its back. */
  | 'dune'
  /** Wind-packed snow: long drifts and the odd wind-scoured scar. */
  | 'drift'
  /** Fen: streaks that run flat, and standing pools between them. */
  | 'reed'
  /** Beach: a fine ripple and shell grit, the smoothest ground there is. */
  | 'shingle'
  /** Old masonry heaps over older plot boundaries. */
  | 'rubble'
  /** Running water. */
  | 'current'
  /** Decking. */
  | 'plank'
  /** A worked track: ruts and the stones that were thrown out of them. */
  | 'rut';

/**
 * Which family of ground a terrain belongs to, for the purposes of a country
 * repainting it.
 *
 * Deliberately coarser than the terrain enum. A country has an opinion about
 * "bare rock" and none whatsoever about the difference between a stone outcrop
 * and an iron seam — that difference is the *baseline* palette's job, and it
 * survives every repaint because the pull is applied to it rather than
 * replacing it.
 */
export type GroundGroup = 'dry' | 'wood' | 'rock' | 'wet' | 'water' | 'built';

const GROUP_OF: Record<number, GroundGroup> = {
  [Terrain.AshPlain]: 'dry',
  [Terrain.Ruin]: 'built',
  [Terrain.Forest]: 'wood',
  [Terrain.StoneOutcrop]: 'rock',
  [Terrain.IronSeam]: 'rock',
  [Terrain.Marsh]: 'wet',
  [Terrain.River]: 'water',
  [Terrain.Road]: 'built',
  [Terrain.Ditch]: 'built',
  [Terrain.Leyline]: 'dry',
  [Terrain.Deep]: 'water',
  [Terrain.Bridge]: 'built',
};

/** The group a terrain belongs to, with dry ground as the fallback. */
export function groupOf(terrain: number): GroundGroup {
  return GROUP_OF[terrain] ?? 'dry';
}

/**
 * Everything the ground layer needs to know about one terrain in one country.
 *
 * `rise` and `rough` are the piece that did not exist before this pass, and
 * they are the settlement's answer to the question the campaign chart's relief
 * asked: what does the generator already know about this ground that nothing
 * ever drew? A stone outcrop *stands up*. A ditch is a hole. A river is the
 * bottom of a cut and its banks are the walls of one. None of that was ever
 * state — it is implied by the terrain class, exactly, and can be derived from
 * it for nothing.
 */
export interface Surface {
  /** The ground's own colour, before any light. */
  base: number;
  /** A lighter ink for the crisp marks the tile textures draw. */
  alt: number;
  /** The dark ink for the same. */
  ink: number;
  /** Height in tiles, relative to open ground. Render-only, derived, unhashed. */
  rise: number;
  /** How much of the country's relief noise this surface carries, 0..1. */
  rough: number;
  /**
   * How much of its own colour this tile keeps through the blur, 0..1.
   *
   * The blur is what turns a tile boundary into a transition, and it is right
   * for everything the ground grew and wrong for everything somebody built: a
   * road is two tiles wide and a 1.5-tile blur erases it. So structures state
   * how hard-edged they are and get their colour back afterwards.
   */
  hard: number;
  /** The grain this surface wears. */
  fabric: FabricKind;
  /** How strongly, 0..1 — the country's own ground usually carries the most. */
  fabricGain: number;
}

/**
 * The baseline surfaces: the temperate hold every settlement in this game was
 * before there were countries.
 *
 * The colours are the twelve that shipped in `C.terr*`, unchanged and read from
 * there rather than copied, because `ui/worldmap.ts` also reads some of them and
 * two files quietly disagreeing about what `terrForest` is would be the sort of
 * fault that takes an afternoon.
 */
const BASE: Record<number, Surface> = {
  [Terrain.AshPlain]: { base: C.terrAsh, alt: 0x2b2823, ink: 0x181614, rise: 0, rough: 1, hard: 0.12, fabric: 'grit', fabricGain: 1 },
  [Terrain.Ruin]: { base: C.terrRuin, alt: 0x393530, ink: 0x1e1c19, rise: 0.1, rough: 0.7, hard: 0.4, fabric: 'rubble', fabricGain: 1 },
  [Terrain.Forest]: { base: C.terrForest, alt: 0x262e21, ink: 0x131711, rise: 0.09, rough: 0.85, hard: 0.2, fabric: 'litter', fabricGain: 1 },
  [Terrain.StoneOutcrop]: { base: C.terrStone, alt: 0x3d3b37, ink: 0x201f1d, rise: 0.5, rough: 1.4, hard: 0.3, fabric: 'scree', fabricGain: 1 },
  [Terrain.IronSeam]: { base: C.terrIron, alt: 0x413628, ink: 0x1f1a14, rise: 0.3, rough: 1.15, hard: 0.3, fabric: 'scree', fabricGain: 0.9 },
  [Terrain.Marsh]: { base: C.terrMarsh, alt: 0x282d25, ink: 0x141712, rise: -0.16, rough: 0.25, hard: 0.18, fabric: 'reed', fabricGain: 1 },
  /**
   * The banks are shallow, and that is a correction rather than a preference.
   *
   * The first cut cut the channel a whole tile into the ground and let the
   * hillshade light the sides of it. Over the blur that is a slope of half a
   * tile per tile at the shore, which arrives as a **bright band a tile and a
   * half wide down the west bank and a dark one down the east** — measured off
   * `cityart-02-water-z22`, where it read as smoke rolling off the river rather
   * than as a bank. Water is not a hillside: what the eye reads as depth is the
   * colour and the surface, and `terrDeep` is already four stops under
   * `terrRiver` and colder. So the cut is shallow and the shore is *hard*
   * instead — a shoreline is one of the few genuinely hard edges on a map, and
   * paying it out over a tile and a half was always the wrong instrument.
   */
  [Terrain.River]: { base: C.terrRiver, alt: 0x1f2d36, ink: 0x0f171c, rise: -0.18, rough: 0.2, hard: 0.66, fabric: 'current', fabricGain: 0.8 },
  [Terrain.Road]: { base: C.terrRoad, alt: 0x3f382e, ink: 0x231f1a, rise: -0.05, rough: 0.3, hard: 0.66, fabric: 'rut', fabricGain: 1 },
  [Terrain.Ditch]: { base: C.terrDitch, alt: 0x24211c, ink: 0x0c0b09, rise: -0.5, rough: 0.4, hard: 0.7, fabric: 'grit', fabricGain: 0.6 },
  [Terrain.Leyline]: { base: C.terrLeyline, alt: 0x2a3145, ink: 0x101321, rise: 0, rough: 0.8, hard: 0.5, fabric: 'grit', fabricGain: 0.7 },
  [Terrain.Deep]: { base: C.terrDeep, alt: 0x14222b, ink: 0x070d11, rise: -0.34, rough: 0.1, hard: 0.66, fabric: 'current', fabricGain: 0.45 },
  [Terrain.Bridge]: { base: C.terrBridge, alt: 0x574733, ink: 0x2a2219, rise: 0.34, rough: 0, hard: 0.95, fabric: 'plank', fabricGain: 1 },
};

/** A pull: a colour to move toward and how far, 0..1. */
interface Pull {
  c: number;
  t: number;
}

export interface BiomeTheme {
  id: string;
  /** How this country repaints each family of ground. Missing = leave it alone. */
  pull: Partial<Record<GroundGroup, Pull>>;
  /** The colour the lit face of a slope goes toward. */
  sun: number;
  /** The colour the shaded face goes toward. */
  shadow: number;
  /** How hard the light bites, 0..1. Snow shows form; a fen has none to show. */
  key: number;
  /**
   * Amplitude of the country's own relief noise, in tiles.
   *
   * This is the settlement's version of `RELIEF_PEAK` on the chart, and the
   * same argument settles it: a field of undulation lit hard is still a field
   * of undulation, and what makes a mountain hold read as one is that its
   * ground has *edges* at a scale of a few tiles.
   */
  relief: number;
  /** The grain open ground wears here — the country's signature. */
  open: FabricKind;
  /** Multiplies every fabric's strength. A shale slope is busier than a beach. */
  grain: number;
  /**
   * What the trees, boulders, wrecks and rubble standing on this ground are
   * tinted by.
   *
   * A multiply, so it can only ever darken and shift — which is the right
   * instrument, because the props were baked once against ash and the job here
   * is to seat them in a different country, not to repaint them. White is
   * "leave it alone" and is what the temperate baseline uses.
   */
  prop: number;
}

/**
 * The countries.
 *
 * Ten, matching `ProvinceTerrain` exactly, plus the baseline any unknown string
 * falls back to — so a country added to the chart tomorrow degrades to the hold
 * this game shipped rather than to a blank map.
 */
export const BIOME_THEMES: Record<string, BiomeTheme> = {
  /** The baseline. Ash, cinder and old field lines: what every hold used to be. */
  plain: {
    id: 'plain',
    pull: {},
    sun: 0xd8cdb6,
    shadow: 0x0b0a09,
    key: 0.5,
    // Raised from 0.18 after looking at `cityart-01-hold-plain-z30`: the
    // baseline country came out as a flat warm haze with the road smeared
    // through it. A temperate plain is not a billiard table, and 0.3 of a tile
    // over a nine-tile wavelength is the difference between ground and fog.
    relief: 0.3,
    open: 'turf',
    grain: 1.12,
    prop: 0xffffff,
  },
  /**
   * The deep wood. Everything under the canopy goes green-dark and damp, and
   * the light barely reaches the floor — `key` is low because a forest floor is
   * lit by a hole in the leaves, not by the sky.
   */
  forest: {
    id: 'forest',
    pull: {
      dry: { c: 0x232a1e, t: 0.62 },
      wood: { c: 0x1a2117, t: 0.4 },
      rock: { c: 0x2a2e26, t: 0.3 },
      wet: { c: 0x1d251c, t: 0.35 },
      water: { c: 0x16231f, t: 0.3 },
      built: { c: 0x2c2c22, t: 0.22 },
    },
    sun: 0xbfc79c,
    shadow: 0x090c07,
    key: 0.42,
    relief: 0.22,
    open: 'litter',
    grain: 1.1,
    prop: 0xd9e2c6,
  },
  /** The same, wetter and with the clearings closed. */
  jungle: {
    id: 'jungle',
    pull: {
      dry: { c: 0x1f2a1c, t: 0.7 },
      wood: { c: 0x172014, t: 0.5 },
      rock: { c: 0x263025, t: 0.35 },
      wet: { c: 0x1a251b, t: 0.45 },
      water: { c: 0x14241d, t: 0.4 },
      built: { c: 0x282c20, t: 0.3 },
    },
    sun: 0xb6c691,
    shadow: 0x070b06,
    key: 0.36,
    relief: 0.25,
    open: 'litter',
    grain: 1.2,
    prop: 0xcfe0b6,
  },
  /**
   * Hills: turf over stone. A little browner, a good deal more shape, and the
   * first country where the relief is doing real work.
   */
  hill: {
    id: 'hill',
    pull: {
      dry: { c: 0x2a291f, t: 0.5 },
      rock: { c: 0x33322c, t: 0.35 },
      wet: { c: 0x22261e, t: 0.25 },
      built: { c: 0x353026, t: 0.2 },
    },
    sun: 0xd6cbaa,
    shadow: 0x0a0908,
    key: 0.62,
    relief: 0.52,
    open: 'turf',
    grain: 1,
    prop: 0xf0ece0,
  },
  /**
   * The high hold. Stone underfoot everywhere, the ground with the most shape
   * in the game, and a hard cold light — this is the country the whole relief
   * layer exists for.
   */
  mountain: {
    id: 'mountain',
    pull: {
      dry: { c: 0x2f2e2b, t: 0.66 },
      wood: { c: 0x24291f, t: 0.4 },
      rock: { c: 0x36352f, t: 0.4 },
      wet: { c: 0x272a26, t: 0.4 },
      water: { c: 0x16242c, t: 0.35 },
      built: { c: 0x35322b, t: 0.3 },
    },
    sun: 0xe0dcd0,
    shadow: 0x08090b,
    /**
     * Pulled back from `key: 0.86, relief: 0.95, grain: 1.25`.
     *
     * Those numbers are what a massif wants in isolation and they broke the one
     * rule `palette.ts` says outranks everything: at the zoom a player plays at,
     * `cityart-01-hold-mountain-z30` was a field of high-contrast grey mottle
     * with a settlement somewhere in it. **Richer ground, not louder ground** —
     * a mountain hold still has by a distance the most shape of any country
     * here, it simply no longer competes with the men standing on it.
     */
    key: 0.7,
    relief: 0.78,
    open: 'scree',
    grain: 1.12,
    prop: 0xe8e8ea,
  },
  /**
   * Frozen. The one country whose fabric *lightens* — on snow the drift is the
   * bright part and the scour is the shadow — and the one that made the luma cap
   * necessary rather than merely tidy.
   *
   * Water is ice: pale, still, and lit like a plate rather than like a current.
   */
  ice: {
    id: 'ice',
    pull: {
      dry: { c: 0x2f343a, t: 0.85 },
      wood: { c: 0x272d2b, t: 0.55 },
      rock: { c: 0x32363a, t: 0.5 },
      wet: { c: 0x2d3438, t: 0.7 },
      water: { c: 0x2b3841, t: 0.6 },
      built: { c: 0x3a3a38, t: 0.45 },
    },
    // A winter sun is *low*, not bright. The first cut of this used a near-white
    // key and put 47% of a snowfield at the luma cap — a flat sheet with no form
    // left in it. What says snow is the colour of the light and the hardness of
    // the shadow, and neither of those is an amount of white.
    sun: 0xaec2d8,
    shadow: 0x0a1120,
    key: 0.58,
    relief: 0.4,
    open: 'drift',
    grain: 0.85,
    prop: 0xdfe8f2,
  },
  /**
   * Sand. Warm light, violet shade — the two together are most of why a dune
   * field reads as heat — and the dune fabric carrying the whole surface, since
   * there is nothing else out there to carry it.
   */
  desert: {
    id: 'desert',
    pull: {
      dry: { c: 0x3a3126, t: 0.82 },
      wood: { c: 0x2d2a1e, t: 0.45 },
      rock: { c: 0x3a342a, t: 0.5 },
      wet: { c: 0x322c22, t: 0.5 },
      water: { c: 0x1b2a2c, t: 0.3 },
      built: { c: 0x3d3529, t: 0.45 },
    },
    sun: 0xf0d9a4,
    shadow: 0x180d12,
    key: 0.7,
    relief: 0.46,
    open: 'dune',
    grain: 1.15,
    prop: 0xf2e0bc,
  },
  /**
   * Fen. The flattest ground in the game and lit accordingly: `key` is the
   * lowest here, because there is nothing for a light to find. What says marsh
   * is the reed grain and the standing water in it.
   */
  marsh: {
    id: 'marsh',
    pull: {
      dry: { c: 0x1f241f, t: 0.66 },
      wood: { c: 0x1a2019, t: 0.35 },
      rock: { c: 0x272a25, t: 0.3 },
      wet: { c: 0x1c231e, t: 0.3 },
      water: { c: 0x141f21, t: 0.4 },
      built: { c: 0x2a2b22, t: 0.3 },
    },
    sun: 0xb9c2ae,
    shadow: 0x080a09,
    key: 0.3,
    relief: 0.08,
    open: 'reed',
    grain: 1.05,
    prop: 0xc8d2c0,
  },
  /** A shore: pale, open, smoothed by weather, and the water is the sea's. */
  coast: {
    id: 'coast',
    pull: {
      dry: { c: 0x332f27, t: 0.6 },
      wood: { c: 0x232719, t: 0.3 },
      rock: { c: 0x35342e, t: 0.3 },
      wet: { c: 0x272a26, t: 0.35 },
      water: { c: 0x15262e, t: 0.5 },
      built: { c: 0x36322a, t: 0.3 },
    },
    sun: 0xdcd6c0,
    shadow: 0x0a0d10,
    key: 0.46,
    relief: 0.14,
    open: 'shingle',
    grain: 0.8,
    prop: 0xeae6d8,
  },
  /**
   * Somebody was here before, and the ground is full of their walls. Warm-grey,
   * with the plot lines of a town under everything.
   */
  ruin: {
    id: 'ruin',
    pull: {
      dry: { c: 0x2c2823, t: 0.62 },
      wood: { c: 0x222519, t: 0.3 },
      rock: { c: 0x35322c, t: 0.35 },
      wet: { c: 0x24251f, t: 0.3 },
      built: { c: 0x37322a, t: 0.35 },
    },
    sun: 0xd4c6a8,
    shadow: 0x0a0807,
    key: 0.55,
    relief: 0.2,
    open: 'rubble',
    grain: 1.1,
    prop: 0xe6dcc8,
  },
};

/** The country this world stands in, or the temperate baseline. */
export function themeFor(biome: string | undefined): BiomeTheme {
  return BIOME_THEMES[biome ?? 'plain'] ?? BIOME_THEMES.plain;
}

/**
 * The surface a terrain wears in this country.
 *
 * Cheap enough to call per tile — a record lookup, one or two `mix`es — but the
 * ground bake reads it once per terrain per chunk through `surfaceTable`
 * anyway, because "cheap enough" times a hundred and forty-seven thousand is
 * how a generator becomes a stall.
 */
export function surfaceFor(theme: BiomeTheme, terrain: number): Surface {
  const b = BASE[terrain] ?? BASE[Terrain.AshPlain];
  const p = theme.pull[groupOf(terrain)];
  /**
   * Open ground takes the country's signature grain; everything else keeps its
   * own. A forest floor is a forest floor in the snow as much as in the wood —
   * what changes there is its *colour*, not what it is made of. Open ground is
   * the opposite: bare ground is precisely the thing a country is.
   */
  const fabric = terrain === Terrain.AshPlain ? theme.open : b.fabric;
  if (!p) return fabric === b.fabric ? b : { ...b, fabric };
  return {
    base: capLuma(mix(b.base, p.c, p.t)),
    alt: capLuma(mix(b.alt, p.c, p.t)),
    ink: mix(b.ink, p.c, p.t * 0.7),
    rise: b.rise,
    rough: b.rough,
    hard: b.hard,
    fabric,
    fabricGain: b.fabricGain,
  };
}

/** Every terrain's surface in this country, indexed by terrain id. */
export function surfaceTable(theme: BiomeTheme): Surface[] {
  const out: Surface[] = [];
  for (const key of Object.keys(BASE)) {
    const t = Number(key);
    out[t] = surfaceFor(theme, t);
  }
  return out;
}

/** Every terrain id the baseline table knows, for the tests and the bakes. */
export const TERRAIN_IDS: readonly number[] = Object.keys(BASE).map(Number);
